import { CurrencyCode } from "@smartrr/shared/currencyCode";
import { IPurchasableVariant, PresentmentPrice } from "@smartrr/shared/entities/PurchasableVariant";
import { PurchaseStateAndDelivery } from "@smartrr/shared/entities/PurchaseState";
import { IPurchaseStateLineItemPricingPolicy } from "@smartrr/shared/entities/PurchaseState/CustomerPurchaseLineItem";
import { ISmartrrSellingPlan, SmartrrSellingPlanPricingPolicy } from "@smartrr/shared/entities/SellingPlanGroup";
import clamp from 'lodash/clamp';
import isNumber from 'lodash/isNumber';

import { IGetPrice } from "..";
import { MoneyHelper, SafeBigInt } from "../utils";
import { CountryCode } from "@smartrr/shared/countryCode";

const getPriceFromCurrency = (
  policy: IPurchaseStateLineItemPricingPolicy | null,
  prices: PresentmentPrice[],
  currency: CurrencyCode,
  customerCountry?: CountryCode
): PresentmentPrice => {
  if (customerCountry && prices.length > 1) {
    return getPriceFromCountry(customerCountry, prices, policy, currency);
  }
  if (policy && policy.basePrice?.currencyCode === currency) {
    return getPriceFromPolicy(policy, currency);
  }

  return getDefaultPrice(prices, currency);
};

const getPriceFromCountry = (
  customerCountry: CountryCode,
  prices: PresentmentPrice[],
  policy: IPurchaseStateLineItemPricingPolicy | null,
  currency: CurrencyCode
) => {
  const countryPrice = prices.find(price => price.priceCountry === customerCountry);
  if (!countryPrice) {
    if (policy && policy.basePrice?.currencyCode === currency) {
      return getPriceFromPolicy(policy, currency);
    }
    return getDefaultPrice(prices, currency);
  }

  return {
    price: Number(countryPrice.price),
    priceCurrency: currency,
  };
};

const getPriceFromPolicy = (policy: IPurchaseStateLineItemPricingPolicy, currency: CurrencyCode) => {
  return {
    price: Number(policy.basePrice.amount) * 100,
    priceCurrency: currency,
  };
};

const getDefaultPrice = (prices: PresentmentPrice[], currency: CurrencyCode) => {
  const price = prices.find(price => price.priceCurrency === currency);
  if (!price) {
    return prices.length
      ? {
          price: Number(prices[0].price),
          priceCurrency: prices[0].priceCurrency,
        }
      : ({
          price: 0,
          priceCurrency: currency,
        } as PresentmentPrice);
  }
  return {
    price: Number(price.price),
    priceCurrency: price.priceCurrency,
  };
};

const calculatePriceChange = (
  price: PresentmentPrice,
  policy: SmartrrSellingPlanPricingPolicy,
  sellingPlan: ISmartrrSellingPlan | null
) => {
  switch (policy.adjustmentType) {
    case "FIXED_AMOUNT": {
      const bigPrice = SafeBigInt.fromDecimal(price.price);
      let billingInterval = 1;
      if (
        sellingPlan?.billingPolicy &&
        sellingPlan?.deliveryPolicy &&
        sellingPlan.billingPolicy.intervalCount !== sellingPlan.deliveryPolicy.intervalCount
      ) {
        billingInterval = sellingPlan.billingPolicy.intervalCount;
      }
      const bigAdjustment = SafeBigInt.fromDecimal((policy.adjustmentValue.amount * 100) / billingInterval);

      const newPrice = MoneyHelper.fromBigInt(bigPrice - bigAdjustment, "up");
      return newPrice > 0 ? newPrice : 0;
    }
    case "PERCENTAGE": {
      const bigPrice = SafeBigInt.fromDecimal(price.price);
      const discountPercentage = clamp(policy.adjustmentValue.percentage, 0, 100);
      const discountedPrice =
        (bigPrice * SafeBigInt.fromDecimal(discountPercentage)) / SafeBigInt.fromDecimal(100);
      const finalPrice = bigPrice - discountedPrice;

      const newPrice = MoneyHelper.fromBigInt(finalPrice, "up");
      return newPrice > 0 ? newPrice : 0;
    }
    case "PRICE": {
      const discountPrice = Number(policy.computedPrice?.amount ?? policy.adjustmentValue.amount ?? 0);
      const newPrice = discountPrice * 100;
      return newPrice > 0 ? newPrice : 0;
    }
    default: {
      return price.price;
    }
  }
};

const findPolicy = <T>(arr: Array<T>, orderNumber: number, cycleCountFn: (policy: T) => number): T | null => {
  /**
   * Go through policies.
   * If next policy doesn't exist, we have reached the end and the current policy is the one we want.
   * If next policy exists, check its order count.
   * If order count > OrderNumber, it's a future policy so we want the current policy.
   * If order count <= OrderNumber, it may or may not be the policy so we continue the search
   */
  return (
    arr.find((_, index) => {
      const nextIndex = index + 1;
      const nextPolicy = arr[nextIndex];
      if (nextPolicy) {
        const cycleCount = cycleCountFn(nextPolicy);
        if (cycleCount > orderNumber) {
          return true;
        }
      } else {
        return true;
      }
      return false;
    }) ?? null
  );
};

const getRelevantPolicy = (
  policy: IPurchaseStateLineItemPricingPolicy | null,
  sellingPlan: ISmartrrSellingPlan | null,
  orderNumber: number | null
) => {
  if (!orderNumber) {
    if (policy && policy.cycleDiscounts && policy.cycleDiscounts.length) {
      return policy.cycleDiscounts[0];
    }
    if (sellingPlan) {
      return sellingPlan.pricingPolicies.length ? sellingPlan.pricingPolicies[0] : null;
    }
    return null;
  }

  if (policy && policy.cycleDiscounts) {
    return findPolicy(policy.cycleDiscounts, orderNumber, policy => {
      return policy.afterCycle;
    });
  }

  if (sellingPlan) {
    return findPolicy(sellingPlan.pricingPolicies, orderNumber, policy => {
      return policy.pricingPolicyType === "SellingPlanFixedPricingPolicy" ? 0 : policy.afterCycle;
    });
  }
  return null;
};

/**
 * Ensures that order number must be provided when policy or selling plans are provided.
 */
export type ValidItemInfoCombinations =
  | {
      policy: null;
      sellingPlan: null;
      orderNumber: null;
    }
  | {
      policy: IPurchaseStateLineItemPricingPolicy;
      sellingPlan: ISmartrrSellingPlan;
      orderNumber: number;
    }
  | {
      policy: IPurchaseStateLineItemPricingPolicy;
      sellingPlan: null;
      orderNumber: number;
    }
  | {
      policy: null;
      sellingPlan: ISmartrrSellingPlan;
      orderNumber: number;
    }
  | {
      policy: IPurchaseStateLineItemPricingPolicy;
      sellingPlan: null;
      orderNumber: null;
    }
  | {
      policy: null;
      sellingPlan: null;
      orderNumber: number;
    };

export const getItemPrice = (
  variant: IPurchasableVariant,
  currency: CurrencyCode,
  { policy, sellingPlan, orderNumber }: ValidItemInfoCombinations,
  countryCode?: CountryCode
): IGetPrice => {
  const initialPrice = getPriceFromCurrency(policy, variant.presentmentPrices, currency, countryCode);
  const defaultResult = {
    initial: initialPrice,
    current: initialPrice,
  };

  if (sellingPlan === null && policy === null) {
    return defaultResult;
  }

  const adjustment = getRelevantPolicy(policy, sellingPlan, orderNumber);

  if (!adjustment) {
    return defaultResult;
  }

  return {
    initial: initialPrice,
    current: {
      price: calculatePriceChange(initialPrice, adjustment as SmartrrSellingPlanPricingPolicy, sellingPlan),
      priceCurrency: currency,
    },
  };
};

/**
 * Gets the item price when a nullable Selling Plan is provided.
 *
 * @param variant Purchasable Variant
 * @param currency Currency code
 * @param selectedPlan Selling Plan (may be null)
 * @param orderNumber Order number of delivery
 * @returns Item price
 */
export const getItemPriceWithSellingPlan = (
  variant: IPurchasableVariant,
  currency: CurrencyCode,
  selectedPlan: ISmartrrSellingPlan | null | undefined,
  orderNumber: number | null,
  policy?: IPurchaseStateLineItemPricingPolicy,
  countryCode?: CountryCode
): IGetPrice => {
  if (selectedPlan && isNumber(orderNumber)) {
    return getItemPrice(
      variant,
      currency,
      {
        policy: policy ?? null,
        sellingPlan: selectedPlan,
        orderNumber,
      },
      countryCode
    );
  }

  return getItemPrice(
    variant,
    currency,
    {
      policy: policy ?? null,
      sellingPlan: null,
      orderNumber,
    },
    countryCode
  );
};

export const getOrderCount = (delivery: PurchaseStateAndDelivery) => {
  return delivery?.purchaseState?.schedule?.totalOrdersCount ?? delivery?.indexFromScheduleStart ?? 1;
};
